import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  HiXMark,
  HiChatBubbleLeftRight,
  HiPaperAirplane,
  HiTrash,
  HiPencilSquare,
  HiUserCircle,
} from 'react-icons/hi2';
import { format } from 'date-fns';

const DEFAULT_STATUS = 'reviewed';
const STATUS_OPTIONS = [
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'approved', label: 'Approved' },
  { value: 'needs_revision', label: 'Needs Revision' },
  { value: 'in_progress', label: 'In Progress' },
];

const SubmissionFeedbackModal = ({
  submission,
  isOpen,
  onClose,
  onAddFeedback,
  onUpdateFeedback,
  onDeleteFeedback,
  currentUser,
  saving = false,
}) => {
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [editingFeedbackId, setEditingFeedbackId] = useState(null);
  const lastSubmissionIdRef = useRef(null);
  const lastEditingIdRef = useRef(null);

  const feedbackEntries = useMemo(() => {
    try {
      if (!submission?.feedback || !Array.isArray(submission.feedback)) return [];

      return [...submission.feedback]
        .filter(entry => entry != null) // Filter out null/undefined entries
        .map(entry => ({
          ...entry,
          // Ensure all required fields exist
          id: entry.id || String(Date.now() + Math.random()),
          comment: entry.comment || '',
          status: entry.status || DEFAULT_STATUS,
          createdByName: entry.createdByName || entry.createdByEmail || 'Team Member',
          createdByEmail: entry.createdByEmail || '',
          createdByRole: entry.createdByRole || '',
          updatedAt: entry.updatedAt || entry.createdAt || null,
        }))
        .sort((a, b) => {
          try {
            // Safely extract dates
            let aDate;
            if (a.updatedAt) {
              if (a.updatedAt.toDate && typeof a.updatedAt.toDate === 'function') {
                aDate = a.updatedAt.toDate();
              } else if (a.updatedAt.seconds) {
                // Firestore timestamp object
                aDate = new Date(a.updatedAt.seconds * 1000);
              } else {
                aDate = new Date(a.updatedAt);
              }
            } else {
              aDate = new Date(0);
            }

            let bDate;
            if (b.updatedAt) {
              if (b.updatedAt.toDate && typeof b.updatedAt.toDate === 'function') {
                bDate = b.updatedAt.toDate();
              } else if (b.updatedAt.seconds) {
                // Firestore timestamp object
                bDate = new Date(b.updatedAt.seconds * 1000);
              } else {
                bDate = new Date(b.updatedAt);
              }
            } else {
              bDate = new Date(0);
            }

            // Check if dates are valid
            if (isNaN(aDate.getTime())) aDate = new Date(0);
            if (isNaN(bDate.getTime())) bDate = new Date(0);

            return bDate - aDate;
          } catch (error) {
            return 0;
          }
        });
    } catch (error) {
      return [];
    }
  }, [submission?.feedback]);

  useEffect(() => {
    if (!isOpen) {
      setComment('');
      setStatus(DEFAULT_STATUS);
      setEditingFeedbackId(null);
      lastSubmissionIdRef.current = null;
      lastEditingIdRef.current = null;
      return;
    }

    const submissionId = submission?.id;
    const isNewSubmission = submissionId !== lastSubmissionIdRef.current;
    const isNewEditingId = editingFeedbackId !== lastEditingIdRef.current;

    if (submission && editingFeedbackId && (isNewEditingId || isNewSubmission)) {
      const match = submission.feedback?.find(entry => entry.id === editingFeedbackId);
      if (match) {
        setComment(match.comment || '');
        setStatus(match.status || DEFAULT_STATUS);
        lastEditingIdRef.current = editingFeedbackId;
        lastSubmissionIdRef.current = submissionId;
      }
    } else if (!editingFeedbackId && isNewSubmission) {
      // Only reset if we're not editing and it's a new submission
      setComment('');
      setStatus(DEFAULT_STATUS);
      lastSubmissionIdRef.current = submissionId;
      lastEditingIdRef.current = null;
    }
  }, [isOpen, editingFeedbackId, submission?.id]);

  // Add error boundary check
  if (!isOpen) {
    return null;
  }

  if (!submission) {
    return null;
  }

  // Ensure submission has required fields
  if (!submission.id) {
    return null;
  }

  const handleSubmit = () => {
    if (!comment.trim()) {
      return;
    }

    const payload = {
      comment: comment.trim(),
      status,
      createdById: currentUser?.uid || null,
      createdByEmail: currentUser?.email || null,
      createdByRole: currentUser?.role || null,
      createdByName: currentUser?.displayName || currentUser?.name || null,
    };

    if (editingFeedbackId) {
      onUpdateFeedback?.(submission.id, editingFeedbackId, payload);
    } else {
      onAddFeedback?.(submission.id, payload);
    }
  };

  const handleEdit = feedbackId => {
    setEditingFeedbackId(feedbackId);
    const match = submission.feedback?.find(entry => entry.id === feedbackId);
    if (match) {
      setComment(match.comment || '');
      setStatus(match.status || DEFAULT_STATUS);
    }
  };

  const handleDelete = feedbackId => {
    onDeleteFeedback?.(submission.id, feedbackId);
  };

  const handleResetForm = () => {
    setComment('');
    setStatus(DEFAULT_STATUS);
    setEditingFeedbackId(null);
  };

  const formatTimestamp = value => {
    if (!value) return 'N/A';

    try {
      let date;

      // Handle Firestore Timestamp
      if (value.toDate && typeof value.toDate === 'function') {
        date = value.toDate();
      }
      // Handle Firestore Timestamp object with seconds
      else if (value.seconds && typeof value.seconds === 'number') {
        date = new Date(value.seconds * 1000);
      }
      // Handle Timestamp object with _seconds
      else if (value._seconds && typeof value._seconds === 'number') {
        date = new Date(value._seconds * 1000);
      }
      // Handle string or number
      else {
        date = new Date(value);
      }

      // Validate date
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      return format(date, 'PPP p');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-black">
              Feedback for {submission.stageName || submission.stage || 'Submission'}
            </h2>
            <p className="text-sm text-gray-600">
              {submission.userEmail || submission.clientEmail || 'Unknown client'} •{' '}
              {formatTimestamp(submission.createdAt)}
            </p>
          </div>
          <button
            onClick={() => {
              handleResetForm();
              onClose?.();
            }}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 p-6">
          {/* Only show feedback form for staff/admins, clients can only view */}
          {(currentUser?.role === 'admin' ||
            currentUser?.role === 'coach' ||
            currentUser?.role === 'moderator' ||
            currentUser?.role === 'superAdmin' ||
            currentUser?.role === 'superAdmins') && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <HiChatBubbleLeftRight className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-black">
                  {editingFeedbackId ? 'Edit Feedback' : 'Add Feedback'}
                </h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={status}
                    onChange={event => setStatus(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Comment</label>
                  <textarea
                    value={comment}
                    onChange={event => setComment(event.target.value)}
                    rows={4}
                    placeholder="Share your feedback and next steps for the client..."
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  {editingFeedbackId && (
                    <button
                      type="button"
                      onClick={handleResetForm}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={saving || !comment.trim()}
                    onClick={handleSubmit}
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <HiPaperAirplane className="h-4 w-4" />
                        {editingFeedbackId ? 'Update Feedback' : 'Add Feedback'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-black">Previous Feedback</h3>
            {feedbackEntries.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
                No feedback recorded yet. Be the first to leave guidance for this client.
              </p>
            ) : (
              feedbackEntries.map(entry => (
                <div
                  key={entry.id}
                  className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <HiUserCircle className="h-4 w-4 text-gray-500" />
                      <span>{entry.createdByName || entry.createdByEmail || 'Team Member'}</span>
                      <span>•</span>
                      <span className="capitalize text-gray-500">{entry.createdByRole || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                        {STATUS_OPTIONS.find(opt => opt.value === entry.status)?.label ||
                          entry.status ||
                          'Reviewed'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTimestamp(entry.updatedAt || entry.createdAt)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">{entry.comment || 'No comment provided'}</p>
                  {/* Only show edit/delete buttons if user has permission (staff/admins) */}
                  {(currentUser?.role === 'admin' ||
                    currentUser?.role === 'coach' ||
                    currentUser?.role === 'moderator' ||
                    currentUser?.role === 'superAdmin' ||
                    currentUser?.role === 'superAdmins' ||
                    entry.createdById === currentUser?.uid) && (
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => handleEdit(entry.id)}
                        className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
                      >
                        <HiPencilSquare className="h-3 w-3" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                      >
                        <HiTrash className="h-3 w-3" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SubmissionFeedbackModal;
